import { DNode } from '@dojo/framework/core/interfaces';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { create, dom, v, w } from '@dojo/framework/core/vdom';
import { Button } from '@dojo/widgets/button';
import has from '@dojo/framework/core/has';

import * as moment from 'moment';

import { Card, CardProperties } from '@holmescorp/card';
import { ExpandedBaseCard } from '@holmescorp/card/cards/widgets/expandedBase';
import { convertToBoolean } from '@holmescorp/common';
import { createNlsMiddleware, exploreToolChange, userstate } from '@holmescorp/lcc-state';
import { subscribe, publish } from '@holmescorp/pub-sub';
import { StudyPlanExtraItem } from '@holmescorp/explore-view/interfaces';

import css from '../css/cert-card.m.css';
import { check_rpc_passed, check_rpc_passed_date, load_cert, print_cert } from '../support/utils';

export interface CertificateCardProperties extends CardProperties<string> {
	title?: string;
	subtitle?: string;
	xqa?: string;
}

export interface CertificateCardICache {
	expanded: boolean;
	certLoaded: boolean;
	earned: boolean;
	completed_date: string | null;
}

const icache = createICacheMiddleware<CertificateCardICache>();

const factory = create({
	icache,
	nls: createNlsMiddleware('certificate'),
	theme,
	userstate
}).properties<CertificateCardProperties>();

export const CertificateCard = factory(({ properties, middleware: { icache, nls, theme, userstate } }): DNode => {
	const {
		xqa,
		key = 'certificate',
		title = 'Certificate of Acheivement',
		mediaSrc = '',
		mediaTitle = title,
		subtitle = '',
		nlsKey = 'certificateCard',
		ispromotedisabled = 'true',
		ispromotable = 'false',
		ispromoted = 'false'
	} = properties();
	const {
		format,
		messages: {
			use_confirmation = '[certificate] use_confirmation',
			print_instructions = '[certificate] print_instructions',
			unearned_instructions = '[certificate] unearned_instructions',
			congratulations = '[certificate] congratulations',
			continue_button_text = '[certificate] continue_button_text',
			print_button_text = '[certificate] print_button_text',
			confirmation_prompt = '[certificate] confirmation_prompt',
			confirmation_title = '[certificate] confirmation_title',
			confirmation_text = '[certificate] confirmation_text'
		}
	} = nls();
	const { messages } = nls();
	const { get, path } = userstate;

	const useConfirmation = convertToBoolean(use_confirmation);
	const { extra: exploreTool } = get(path('toolstate', 'explore'));
	const certificateAttested = exploreTool && exploreTool.certificateAttested && exploreTool.certificateAttestDate;
	const { survey } = get(path('toolstate', 'posttest')) || {};
	const surveyCompleted = has('use-quiz-survey') ? survey?.completed || false : true;
	const expanded = icache.getOrSet('expanded', false);
	const certLoaded = icache.getOrSet('certLoaded', false);

	const themeCss = theme.classes(css);

	subscribe('cert/loaded', async (): Promise<void> => {
		icache.set('certLoaded', true);
	});

	const earned = icache.getOrSet('earned', async (): Promise<boolean> => {
		const posttest = get(path('posttest'));
		const userid = get(path('user_id'));
		return posttest && posttest.passed ? true : await check_rpc_passed(userid);
	});

	const completed_date = icache.getOrSet('completed_date', async (): Promise<string | null> => {
		return await check_rpc_passed_date(get(path('user_id')));
	});

	const studyplan = get(path('toolstate', 'studyplan'));
	const { extra = [] } = studyplan;
	const extraItem = extra.find((item: StudyPlanExtraItem) => item.key === 'certificateCard');

	if (extraItem && extraItem?.initial) {
		if (!certLoaded || (!earned && !(useConfirmation && certificateAttested))) {
			publish('tool:studyplan:modify', {
				key: nlsKey,
				type: 'resource',
				state: 'promoted',
				action: 'add'
			});
		}
	}
	//NOTE: This card was made to display both a subtitle and instructions if available.
	// This resulted in a card that had too much content and didn't display very well.
	// In the future if both are required one solution might be to add a tooltip that displays instructions.
	const instructions = earned
		? `${congratulations}\n\n${print_instructions}`
		: certificateAttested
		? print_instructions
		: unearned_instructions;
	const subtitle_text = subtitle
		? v('div', { classes: [themeCss.subtitle] }, [subtitle])
		: v('div', { classes: [themeCss.subtitle], innerHTML: instructions });
	const content = v('div', { classes: themeCss.content }, [
		title ? v('div', { classes: [themeCss.title] }, [title]) : null,
		subtitle_text,
		// v('div', { classes: [themeCss.subtitle, 'instructions'], title: instructions }, [instructions]),
		v('div', { classes: [themeCss.actions] }, [
			useConfirmation && !certificateAttested
				? w(
						Button,
						{
							onClick: (): void => {
								icache.set('expanded', true);
							}
						},
						[
							{
								label: continue_button_text
							}
						]
				  )
				: w(
						Button,
						{
							disabled:
								!certLoaded ||
								(has('use-micro-view') && !surveyCompleted) ||
								(!earned && !(useConfirmation && certificateAttested)),
							onClick: (): void => {
								print_cert();
							}
						},
						[
							{
								label: print_button_text
							}
						]
				  )
		])
	]);

	const expandedContent = v('div', { classes: [theme.variant(), themeCss.root, 'expanded'] }, [
		v('div', { classes: [themeCss.content] }, [
			v('div', { innerHTML: confirmation_prompt }),
			v('div', { classes: [themeCss.actions] }, [
				w(
					Button,
					{
						onClick: (): void => {
							exploreToolChange({
								extra: { certificateAttested: true, certificateAttestDate: moment().format('l') }
							});
							icache.set('expanded', false);
						}
					},
					[{ label: confirmation_text }]
				)
			])
		])
	]);

	const printElements = (): DNode => {
		const _canvas = document.createElement('canvas');
		const _printNode = document.createElement('iframe');
		const learnerName = get(path('fullname'));

		return v('div', {}, [
			dom({ node: _canvas as HTMLCanvasElement }),
			dom({
				node: _printNode as HTMLIFrameElement,
				onAttach: () =>
					load_cert({
						messages,
						learnerName,
						completed_date: (completed_date
							? moment(completed_date).format('l')
							: certificateAttested) as string,
						canvas: _canvas,
						printNode: _printNode
					})
			})
		]);
	};

	return v('div', { xqa, classes: [theme.variant(), themeCss.root, 'expanded'] }, [
		expanded
			? w(
					ExpandedBaseCard,
					{
						expanded: icache.get('expanded') || false,
						title: confirmation_title,
						xqa: `${key}-expanded`,
						onRequestClose: (): void => {
							icache.set('expanded', false);
						}
					},
					[{ content: expandedContent }]
			  )
			: null,
		w(
			Card,
			{
				xqa: `card-${title}-${key}`,
				mediaSrc,
				mediaTitle,
				nlsKey,
				ispromotable,
				ispromotedisabled,
				ispromoted
			},
			[
				{
					content
				}
			]
		),
		(useConfirmation && !certificateAttested) || (!completed_date && !certificateAttested) ? null : printElements()
	]);
});

export default CertificateCard;
